import { Color3, Scene, SpotLight, Vector3 } from '@babylonjs/core';

interface CarPose {
  pos: Vector3;
  yaw: number;
}

export type HeadlightCameraMode = 'chase' | 'topdown';

const CAR_COUNT = 8; // nearest 8 AI cars get a real pair of spotlights
const POOL_SIZE = CAR_COUNT * 2; // 2 lights per car (left + right headlight)
const COLOR = new Color3(1.0, 0.95, 0.85);
const SIDE_OFFSET = 0.55; // half-distance between the two headlights, meters

interface Preset {
  forwardOffset: number;
  /** Vertical offset from the car body's center (positive = above). */
  heightOffset: number;
  /** Downward tilt of the cone axis, in radians. 0 = straight forward. */
  tiltRad: number;
  /** Cone half-angle in radians. */
  angle: number;
  /** Distance after which the light fully attenuates to 0. Longer = beam reaches further. */
  range: number;
  intensity: number;
  /** Cone-radial concentration. Higher = brighter at center, softer fade toward cone edge. */
  exponent: number;
}

// Chase: high mount on the bumper, narrow beam aimed nearly horizontal — reads as a real
// headlight throwing a cone forward through the night.
// Top-down: shallower tilt + narrower beam + long range so the cone projects forward as
// a long elongated patch instead of clipping the ground in a tight ellipse. Higher exponent
// softens the cone's outer edge so it fades out instead of ending abruptly.
const PRESETS: Record<HeadlightCameraMode, Preset> = {
  chase: {
    forwardOffset: 2.0,
    heightOffset: 0.7,
    tiltRad: (3 * Math.PI) / 180,
    angle: Math.PI / 4,
    range: 60,
    intensity: 4,
    exponent: 4,
  },
  topdown: {
    forwardOffset: 1.0,
    heightOffset: 1.0,
    tiltRad: (20 * Math.PI) / 180,
    angle: Math.PI / 4.5,
    range: 60,
    intensity: 4,
    exponent: 4,
  },
};

export class HeadlightSystem {
  private playerLights: [SpotLight, SpotLight];
  private pool: SpotLight[] = [];
  private enabled = false;
  private preset: Preset = PRESETS.chase;

  constructor(scene: Scene) {
    this.playerLights = [
      this.makeLight(scene, 'headlight-player-L'),
      this.makeLight(scene, 'headlight-player-R'),
    ];
    for (let i = 0; i < POOL_SIZE; i++) {
      this.pool.push(this.makeLight(scene, `headlight-pool-${i}`));
    }
    this.setEnabled(false);
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    this.playerLights[0].setEnabled(on);
    this.playerLights[1].setEnabled(on);
    if (!on) {
      for (const l of this.pool) l.setEnabled(false);
    }
  }

  /** Swap to the geometry preset that looks right for the active camera mode. */
  setCameraMode(mode: HeadlightCameraMode): void {
    this.preset = PRESETS[mode];
    // angle/range/intensity/exponent are per-light properties — push them to every light
    // immediately so the next frame's update() uses the new optics.
    applyPresetOptics(this.playerLights[0], this.preset);
    applyPresetOptics(this.playerLights[1], this.preset);
    for (const l of this.pool) applyPresetOptics(l, this.preset);
  }

  update(
    playerPos: Vector3,
    playerYaw: number,
    cameraPos: Vector3,
    trafficCars: CarPose[],
  ): void {
    if (!this.enabled) return;

    aimPair(this.playerLights, playerPos, playerYaw, this.preset);

    if (trafficCars.length === 0) {
      for (const l of this.pool) l.setEnabled(false);
      return;
    }

    const ranked = trafficCars
      .map((c) => ({
        c,
        d2:
          (c.pos.x - cameraPos.x) * (c.pos.x - cameraPos.x) +
          (c.pos.z - cameraPos.z) * (c.pos.z - cameraPos.z),
      }))
      .sort((a, b) => a.d2 - b.d2)
      .slice(0, CAR_COUNT);

    for (let i = 0; i < ranked.length; i++) {
      const left = this.pool[i * 2]!;
      const right = this.pool[i * 2 + 1]!;
      left.setEnabled(true);
      right.setEnabled(true);
      aimPair([left, right], ranked[i]!.c.pos, ranked[i]!.c.yaw, this.preset);
    }
    for (let i = ranked.length * 2; i < this.pool.length; i++) {
      this.pool[i]!.setEnabled(false);
    }
  }

  private makeLight(scene: Scene, name: string): SpotLight {
    const light = new SpotLight(
      name,
      Vector3.Zero(),
      new Vector3(0, 0, 1),
      this.preset.angle,
      this.preset.exponent,
      scene,
    );
    light.diffuse = COLOR;
    light.specular = COLOR;
    applyPresetOptics(light, this.preset);
    light.setEnabled(false);
    return light;
  }
}

function applyPresetOptics(light: SpotLight, preset: Preset): void {
  light.angle = preset.angle;
  light.range = preset.range;
  light.intensity = preset.intensity;
  light.exponent = preset.exponent;
}

function aimPair(
  pair: [SpotLight, SpotLight] | SpotLight[],
  pos: Vector3,
  yaw: number,
  preset: Preset,
): void {
  const fwdX = Math.sin(yaw);
  const fwdZ = Math.cos(yaw);
  // Right vector (perpendicular to forward in the xz plane).
  const rightX = Math.cos(yaw);
  const rightZ = -Math.sin(yaw);

  // Light Y rides the chassis. The car is on the road, so chassis Y is reliably above the
  // road surface regardless of how elevated the road is over the terrain underneath.
  // Suspension bounces translate to small projection shifts; the steep topdown tilt makes
  // those shifts barely visible (Δ_horizontal = Δ_vertical / tan(tilt), which approaches
  // 1:1 only at 45°+).
  const lightY = pos.y + preset.heightOffset;

  const cx = pos.x + fwdX * preset.forwardOffset;
  const cz = pos.z + fwdZ * preset.forwardOffset;

  const left = pair[0]!;
  const right = pair[1]!;
  left.position.set(cx + rightX * SIDE_OFFSET, lightY, cz + rightZ * SIDE_OFFSET);
  right.position.set(cx - rightX * SIDE_OFFSET, lightY, cz - rightZ * SIDE_OFFSET);

  const cos = Math.cos(preset.tiltRad);
  const sin = Math.sin(preset.tiltRad);
  left.direction.set(fwdX * cos, -sin, fwdZ * cos);
  right.direction.set(fwdX * cos, -sin, fwdZ * cos);
}
